/**
 * CreateForCustomerPage — center operator creates a shipment for a
 * walk-in customer in 3 steps:
 *
 *   Step 1: enter customer phone → POST start-consent → OTP sent
 *   Step 2: customer reads OTP, operator types it → POST verify-consent
 *           → consent_token returned
 *   Step 3: shipment form (same fields a regular sender sees) → POST create
 *           → success page with tracking number + print receipt
 *
 * Mirrors the Flutter `center/create_shipment_for_customer_page.dart`
 * (Phase 3, separate file). Uses the existing TextField/Button/Dialog
 * primitives — no new design system pieces.
 */
import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight, Phone, Send, Package, CheckCircle2, AlertCircle,
  Hash, Printer, Copy, RefreshCw,
} from 'lucide-react';
import {
  useStartConsent, useVerifyConsent, useCreateDelegatedShipment,
} from '@/hooks/useCenterDelegated';
import { AppLayout } from '@/components/layout/AppLayout';
import { Button } from '@/components/common/Button';
import { TextField } from '@/components/common/TextField';
import type { StartConsentResult, VerifyConsentResult } from '@/api/centerDelegated';

const FIELD_CLASSES =
  'w-full rounded-xl border border-border bg-surface px-4 py-3 text-text-primary outline-none transition focus:border-primary';

type Step = 'phone' | 'otp' | 'shipment' | 'done';

export default function CreateForCustomerPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('phone');
  const [error, setError] = useState<string | null>(null);

  // Step 1 state
  const [phone, setPhone] = useState('');
  const [consent, setConsent] = useState<StartConsentResult | null>(null);

  // Step 2 state
  const [otp, setOtp] = useState('');
  const [verified, setVerified] = useState<VerifyConsentResult | null>(null);

  // Step 3 state — shipment form
  const [form, setForm] = useState({
    receiverName: '',
    receiverPhone: '',
    receiverAddress: '',
    receiverCity: '',
    pickupAddress: '',
    pickupCity: '',
    packageDescription: '',
    weight: 1,
    quantity: 1,
    estimatedValue: 0,
    isFragile: false,
    requiresRefrigeration: false,
    notes: '',
    paymentMethod: 'cash',
    paidBy: 'sender' as 'sender' | 'receiver',
  });

  // Step 4 result
  const [createdShipment, setCreatedShipment] = useState<any | null>(null);

  const startConsent = useStartConsent();
  const verifyConsent = useVerifyConsent();
  const createShipment = useCreateDelegatedShipment();

  const submitPhone = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!phone.trim()) return;
    try {
      const r = await startConsent.mutateAsync({ phoneNumber: phone.trim() });
      setConsent(r);
      setStep('otp');
    } catch (e: any) {
      setError(e?.message || 'فشل إرسال الرمز');
    }
  };

  const submitOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!consent || !otp.trim()) return;
    try {
      const r = await verifyConsent.mutateAsync({
        consentRequestId: consent.consentRequestId,
        code: otp.trim(),
      });
      setVerified(r);
      setStep('shipment');
    } catch (e: any) {
      setError(e?.message || 'الرمز غير صحيح');
    }
  };

  const submitShipment = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!verified || !consent) return;
    if (!form.receiverName.trim() || !form.receiverPhone.trim() || !form.packageDescription.trim()) {
      setError(t('fill_required_fields', 'يرجى ملء كل الحقول الإلزامية.'));
      return;
    }
    try {
      const r = await createShipment.mutateAsync({
        consentToken: verified.consentToken,
        consentRequestId: consent.consentRequestId,
        shipment: form,
      });
      setCreatedShipment(r.shipment);
      setStep('done');
    } catch (e: any) {
      setError(e?.message || 'فشل إنشاء الشحنة');
    }
  };

  const reset = () => {
    setStep('phone');
    setPhone('');
    setConsent(null);
    setOtp('');
    setVerified(null);
    setCreatedShipment(null);
    setError(null);
  };

  return (
    <AppLayout>
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-text-secondary mb-4 hover:text-primary"
      >
        <ArrowRight size={18} /> {t('back', 'رجوع')}
      </button>

      <div className="max-w-2xl mx-auto">
        <h1 className="text-xl font-bold text-primary mb-1 flex items-center gap-2">
          <Package size={20} /> {t('create_for_customer_title', 'إنشاء شحنة لعميل')}
        </h1>
        <p className="text-text-secondary text-sm mb-5">
          {t('create_for_customer_subtitle', 'الزبون أمامك في المركز ولا يعرف يستخدم التطبيق؟ أنشئ الشحنة باسمه بعد موافقته بالـ OTP.')}
        </p>

        <Stepper current={step} />

        {error && (
          <div className="bg-error/10 border border-error/30 text-error rounded-xl p-3 text-sm flex items-center gap-2 mb-4">
            <AlertCircle size={16} /> {error}
          </div>
        )}

        {step === 'phone' && (
          <PhoneStep
            phone={phone}
            setPhone={setPhone}
            loading={startConsent.isPending}
            onSubmit={submitPhone}
          />
        )}
        {step === 'otp' && consent && (
          <OtpStep
            consent={consent}
            otp={otp}
            setOtp={setOtp}
            loading={verifyConsent.isPending}
            onSubmit={submitOtp}
            onResend={() => { setOtp(''); setStep('phone'); }}
          />
        )}
        {step === 'shipment' && verified && (
          <ShipmentStep
            customerPhone={verified.customerPhone}
            form={form}
            setForm={setForm}
            loading={createShipment.isPending}
            onSubmit={submitShipment}
          />
        )}
        {step === 'done' && createdShipment && (
          <DoneStep shipment={createdShipment} onReset={reset} />
        )}
      </div>
    </AppLayout>
  );
}

function Stepper({ current }: { current: Step }) {
  const steps: { key: Step; label: string }[] = [
    { key: 'phone',    label: 'رقم العميل' },
    { key: 'otp',      label: 'تأكيد العميل' },
    { key: 'shipment', label: 'بيانات الشحنة' },
    { key: 'done',     label: 'تمّت' },
  ];
  const idx = steps.findIndex((s) => s.key === current);
  return (
    <div className="flex items-center mb-6">
      {steps.map((s, i) => (
        <div key={s.key} className="flex items-center flex-1 last:flex-none">
          <div className="flex flex-col items-center">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${
              i < idx ? 'bg-success text-white' :
              i === idx ? 'bg-primary text-white' : 'bg-border/40 text-text-tertiary'
            }`}>
              {i < idx ? <CheckCircle2 size={16} /> : i + 1}
            </div>
            <span className={`text-xs mt-1 ${i === idx ? 'text-primary font-bold' : 'text-text-tertiary'}`}>
              {s.label}
            </span>
          </div>
          {i < steps.length - 1 && (
            <div className={`flex-1 h-0.5 mx-2 ${i < idx ? 'bg-success' : 'bg-border/40'}`} />
          )}
        </div>
      ))}
    </div>
  );
}

function PhoneStep({
  phone, setPhone, loading, onSubmit,
}: {
  phone: string;
  setPhone: (v: string) => void;
  loading: boolean;
  onSubmit: (e: React.FormEvent) => void;
}) {
  const { t } = useTranslation();
  return (
    <form onSubmit={onSubmit} className="bg-surface border border-border rounded-2xl p-5 space-y-4">
      <div className="flex items-center gap-3 mb-2">
        <div className="bg-primary/10 text-primary rounded-full p-2">
          <Phone size={18} />
        </div>
        <h2 className="font-bold text-text-primary">{t('step1_title', 'أدخل رقم هاتف العميل')}</h2>
      </div>
      <p className="text-sm text-text-secondary">
        {t('step1_hint', 'سنرسل رمز تحقّق على هاتفه. اطلب منه قراءة الرمز لك.')}
      </p>
      <TextField
        label={t('customer_phone', 'رقم هاتف العميل') + ' *'}
        type="tel"
        value={phone}
        onChange={(e) => setPhone(e.target.value)}
        placeholder="+963 9XX XXX XXX"
        dir="ltr"
        required
        autoFocus
      />
      <Button variant="primary" loading={loading} type="submit" fullWidth>
        <Send size={16} className="ml-1" />
        {t('send_otp_to_customer', 'إرسال رمز التحقّق للعميل')}
      </Button>
    </form>
  );
}

function OtpStep({
  consent, otp, setOtp, loading, onSubmit, onResend,
}: {
  consent: StartConsentResult;
  otp: string;
  setOtp: (v: string) => void;
  loading: boolean;
  onSubmit: (e: React.FormEvent) => void;
  onResend: () => void;
}) {
  const { t } = useTranslation();
  return (
    <form onSubmit={onSubmit} className="bg-surface border border-border rounded-2xl p-5 space-y-4">
      <div className="flex items-center gap-3 mb-2">
        <div className="bg-primary/10 text-primary rounded-full p-2">
          <CheckCircle2 size={18} />
        </div>
        <h2 className="font-bold text-text-primary">{t('step2_title', 'تأكيد موافقة العميل')}</h2>
      </div>
      <div className="bg-info/5 border border-info/20 rounded-xl p-3 text-sm">
        <p className="text-text-primary font-semibold mb-1">
          ✉️ {t('otp_sent_via', 'أُرسل رمز التحقّق عبر')} {consent.channel === 'whatsapp' ? 'WhatsApp' : 'SMS'}
        </p>
        <p className="text-text-secondary">
          {t('otp_to', 'إلى')}: <span dir="ltr">{consent.customerPhone}</span>
        </p>
        <p className="text-text-tertiary text-xs mt-1">
          {t('otp_expiry_hint', 'صلاحيته 5 دقائق فقط. اطلب من العميل أن يقرأ الرمز لك الآن.')}
        </p>
      </div>
      <TextField
        label={t('enter_otp_label', 'الرمز الذي قرأه العميل') + ' *'}
        type="text"
        value={otp}
        onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
        placeholder="123456"
        dir="ltr"
        inputMode="numeric"
        maxLength={6}
        required
        autoFocus
      />
      <div className="flex gap-2">
        <Button variant="primary" loading={loading} disabled={otp.length !== 6} type="submit">
          {t('verify_consent', 'تأكيد')}
        </Button>
        <Button variant="ghost" type="button" onClick={onResend}>
          <RefreshCw size={14} className="ml-1" />
          {t('resend_otp', 'إعادة الإرسال')}
        </Button>
      </div>
    </form>
  );
}

function ShipmentStep({
  customerPhone, form, setForm, loading, onSubmit,
}: {
  customerPhone: string;
  form: any;
  setForm: (v: any) => void;
  loading: boolean;
  onSubmit: (e: React.FormEvent) => void;
}) {
  const { t } = useTranslation();
  const set = (k: string, v: any) => setForm({ ...form, [k]: v });
  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div className="bg-success/5 border border-success/30 rounded-xl p-3 text-sm">
        <CheckCircle2 size={14} className="inline text-success ml-1" />
        {t('customer_consented', 'وافق العميل')} <span dir="ltr" className="font-bold">{customerPhone}</span>
        {' · '}
        {t('fill_shipment_now', 'اِملأ بيانات الشحنة كأنك العميل')}
      </div>

      {/* Receiver section */}
      <Section title={t('receiver_info', 'بيانات المستلم')}>
        <TextField
          label={t('receiver_name', 'اسم المستلم') + ' *'}
          value={form.receiverName}
          onChange={(e) => set('receiverName', e.target.value)}
          required
        />
        <TextField
          label={t('receiver_phone', 'هاتف المستلم') + ' *'}
          type="tel"
          value={form.receiverPhone}
          onChange={(e) => set('receiverPhone', e.target.value)}
          dir="ltr"
          required
        />
        <div className="grid grid-cols-2 gap-3">
          <TextField
            label={t('receiver_city', 'مدينة المستلم')}
            value={form.receiverCity}
            onChange={(e) => set('receiverCity', e.target.value)}
          />
          <TextField
            label={t('receiver_address', 'عنوان المستلم')}
            value={form.receiverAddress}
            onChange={(e) => set('receiverAddress', e.target.value)}
          />
        </div>
      </Section>

      {/* Pickup */}
      <Section title={t('pickup_info', 'بيانات الاستلام')}>
        <div className="grid grid-cols-2 gap-3">
          <TextField
            label={t('pickup_city', 'مدينة الاستلام')}
            value={form.pickupCity}
            onChange={(e) => set('pickupCity', e.target.value)}
          />
          <TextField
            label={t('pickup_address', 'عنوان الاستلام')}
            value={form.pickupAddress}
            onChange={(e) => set('pickupAddress', e.target.value)}
          />
        </div>
      </Section>

      {/* Package */}
      <Section title={t('package_info', 'بيانات الطرد')}>
        <TextField
          label={t('description', 'الوصف') + ' *'}
          value={form.packageDescription}
          onChange={(e) => set('packageDescription', e.target.value)}
          placeholder={t('package_desc_ph', 'مثال: ملابس، إلكترونيات، طعام...')}
          required
        />
        <div className="grid grid-cols-3 gap-3">
          <TextField
            label={t('weight_kg', 'الوزن (kg)')}
            type="number"
            value={form.weight}
            onChange={(e) => set('weight', Number(e.target.value))}
            min={0}
            step={0.01}
            dir="ltr"
          />
          <TextField
            label={t('quantity', 'العدد')}
            type="number"
            value={form.quantity}
            onChange={(e) => set('quantity', Number(e.target.value))}
            min={1}
            dir="ltr"
          />
          <TextField
            label={t('estimated_value', 'قيمة البضاعة')}
            type="number"
            value={form.estimatedValue}
            onChange={(e) => set('estimatedValue', Number(e.target.value))}
            min={0}
            dir="ltr"
          />
        </div>
        <div className="flex gap-4 flex-wrap">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.isFragile}
              onChange={(e) => set('isFragile', e.target.checked)}
              className="w-4 h-4 accent-primary"
            />
            <span className="text-sm">{t('is_fragile', 'قابل للكسر')}</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.requiresRefrigeration}
              onChange={(e) => set('requiresRefrigeration', e.target.checked)}
              className="w-4 h-4 accent-primary"
            />
            <span className="text-sm">{t('requires_refrigeration', 'يحتاج تبريد')}</span>
          </label>
        </div>
      </Section>

      {/* Payment */}
      <Section title={t('payment_info', 'الدفع')}>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('payment_method', 'طريقة الدفع')}>
            <select
              value={form.paymentMethod}
              onChange={(e) => set('paymentMethod', e.target.value)}
              className={FIELD_CLASSES}
            >
              <option value="cash">{t('cash', 'نقد')}</option>
              <option value="prepaid_wallet">{t('wallet', 'محفظة')}</option>
            </select>
          </Field>
          <Field label={t('paid_by', 'الدافع')}>
            <select
              value={form.paidBy}
              onChange={(e) => set('paidBy', e.target.value)}
              className={FIELD_CLASSES}
            >
              <option value="sender">{t('paid_by_sender', 'المرسل')}</option>
              <option value="receiver">{t('paid_by_receiver', 'المستلم')}</option>
            </select>
          </Field>
        </div>
      </Section>

      <Section title={t('notes', 'ملاحظات (اختياري)')}>
        <textarea
          value={form.notes}
          onChange={(e) => set('notes', e.target.value)}
          className={FIELD_CLASSES + ' min-h-[60px]'}
          placeholder={t('notes_ph', 'أي ملاحظة للسائق أو الإدارة...')}
        />
      </Section>

      <Button variant="primary" loading={loading} type="submit" fullWidth>
        <Package size={16} className="ml-1" />
        {t('create_shipment', 'إنشاء الشحنة')}
      </Button>
    </form>
  );
}

function DoneStep({ shipment, onReset }: { shipment: any; onReset: () => void }) {
  const { t } = useTranslation();
  const tracking = shipment.tracking_number || shipment.trackingNumber;
  return (
    <div className="bg-success/5 border-2 border-success/40 rounded-2xl p-6 text-center">
      <div className="bg-success/20 rounded-full w-16 h-16 mx-auto flex items-center justify-center mb-3">
        <CheckCircle2 size={32} className="text-success" />
      </div>
      <h2 className="text-lg font-bold text-text-primary mb-1">
        {t('shipment_created_for_customer', 'تمّ إنشاء الشحنة')}
      </h2>
      <p className="text-text-secondary mb-4 text-sm">
        {t('shipment_visible_to_customer', 'الشحنة الآن مرئية في تطبيق العميل مع شارة "أنشأها مركز X" + كل التفاصيل.')}
      </p>
      <div className="bg-surface rounded-xl p-3 mb-4 inline-block">
        <div className="text-xs text-text-tertiary mb-1">{t('tracking_number', 'رقم التتبّع')}</div>
        <div className="flex items-center gap-2 justify-center">
          <Hash size={14} className="text-text-tertiary" />
          <span className="font-bold text-lg text-primary" dir="ltr">{tracking}</span>
          <button
            onClick={() => navigator.clipboard?.writeText(String(tracking))}
            className="text-text-tertiary hover:text-primary p-1"
            aria-label="copy"
            title={t('copy', 'نسخ')}
          >
            <Copy size={14} />
          </button>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 justify-center">
        <Button variant="primary" onClick={() => window.print()}>
          <Printer size={14} className="ml-1" />
          {t('print_receipt', 'طباعة إيصال')}
        </Button>
        <Link to={`/shipments/${shipment.id}`}>
          <Button variant="secondary">
            {t('view_shipment', 'فتح الشحنة')}
          </Button>
        </Link>
        <Button variant="ghost" onClick={onReset}>
          {t('create_another', 'إنشاء أخرى')}
        </Button>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-surface border border-border rounded-2xl p-4 space-y-3">
      <h3 className="text-sm font-bold text-text-secondary">{title}</h3>
      {children}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs text-text-secondary mb-1 block">{label}</span>
      {children}
    </label>
  );
}
